import React, { useState, useMemo } from 'react'
import { usePerformanceHierarchy } from '../hooks/usePerformanceHierarchy' 
import { Card } from '@dhis2/ui'
import StatusBadge from './StatusBadge'
import './Objective.css'

const Objectives = ({ orgUnitId, currentUser, performanceData }) => {
  // const { loading, error, data } = usePerformanceHierarchy(orgUnitId)
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [viewMode, setViewMode] = useState('summary');
  const [searchTerm, setSearchTerm] = useState('');

  const data = performanceData; // Use the data from props
  const loading = false; // Since data is passed as prop
  const error = null;

  const calculateObjectiveStatus = (progress, hasTasks) => {
  if (!hasTasks) return 'Not Started';
  if (progress >= 100) return 'Completed';
  if (progress >= 80) return 'On Track';
  if (progress >= 40) return 'At Risk';
  return 'Behind Schedule';
};

  const ExpandIcon = ({ expanded }) => (
    <span style={{ 
      display: 'inline-block', 
      width: 16, 
      height: 16, 
      textAlign: 'center',
      fontSize: '12px',
      fontWeight: 'bold',
      color: '#6b7280',
      lineHeight: '16px'
    }}>
      {expanded ? '−' : '+'}
    </span>
  )

  // Enhanced name extraction with task support
  const extractName = (dataValues, programStage) => {
    const nameDataElements = {
      'VIBtpvkpHFR': 'EuN5ztFnXsQ', // Objective Name
      'BJb3qTgbcTF': 'obapz6Gnyla', // KRA Name
      'ivyCLjNLFrd': 'rQqF75V9t7K', // KPI Description (fallback to KPI Name)
      'Nv9nYNcBTdM': 'I0QtZkMbzxN'  // Task Name
    }

    const nameElement = nameDataElements[programStage]
    const value = dataValues.find(dv => dv.dataElement === nameElement)?.value

    if (value && value.trim() !== '') {
      return value
    }

    // Fallback to KPI Name if Description is empty
    if (programStage === 'ivyCLjNLFrd') {
      const kpiName = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value
      if (kpiName && kpiName.trim() !== '') {
        return kpiName
      }
    }

    const fallbackName = programStage === 'BJb3qTgbcTF' ? 'Unnamed KRA' : 
                        programStage === 'ivyCLjNLFrd' ? 'Unnamed KPI' : 
                        programStage === 'Nv9nYNcBTdM' ? 'Unnamed Task' : 'Unnamed Objective'
    
    return fallbackName
  }

 

  const processedData = useMemo(() => {
  if (!data) return { hierarchy: [], positionMetrics: [], summary: { positionMetrics: [] }, tasksByObjective: {}, kpisByObjective: {} }

  console.log('🔍 DEBUG - ALL Objectives:', data.objectives?.events?.map(e => ({
    name: extractName(e.dataValues, 'VIBtpvkpHFR'),
    orgUnit: e.orgUnit,
    orgUnitName: e.orgUnitName,
    positionTei: e.trackedEntityInstance
  })));

  console.log('🔍 DEBUG - Data structure:', {
    objectives: data.objectives?.events?.length,
    positions: data.positions?.trackedEntityInstances?.length,
    staff: data.staff?.trackedEntityInstances?.length,
    positionHistory: data.positionHistory?.events?.length,
    kpis: data.kpis?.events?.length,
    tasks: data.tasks?.events?.length
  })

  // ========== STEP 1: ENHANCED POSITION MAPPING ==========
  const positionIdToTitleMap = {};
  const positionTeiToStaffTeiMap = {};  // Position TEI → Staff TEI
  const staffTeiToPositionTeiMap = {};  // Staff TEI → Position TEI
  const positionToStaffMap = {};
  const userIdToStaffTeiMap = {};
  const staffIdToNameMap = {};
  const staffTeiToUserIdMap = {};

  // Process positions with both TEI and user ID support
  if (data.positions?.trackedEntityInstances) {
    data.positions.trackedEntityInstances.forEach(position => {
      const positionTeiId = position.trackedEntityInstance;
      
      const positionStatusAttr = position.attributes?.find(attr => 
        attr.attribute === 'Ch7TyL91heU' // Position Status
      );
      
      // Only process ACTIVE positions
      const isActive = positionStatusAttr?.value === 'Active' || 
                      positionStatusAttr?.value === 'active' || 
                      !positionStatusAttr?.value;
      
      if (positionTeiId && isActive) {
        const positionTitleAttr = position.attributes?.find(attr => 
          attr.attribute === 'NWe61Sa41CS' // Position Title
        );
        
        positionIdToTitleMap[positionTeiId] = positionTitleAttr?.value || `Position-${positionTeiId.substring(0, 8)}`;
        
        // Get current incumbent - could be User ID OR Staff TEI!
        const incumbentAttr = position.attributes?.find(attr => 
          attr.attribute === 'V66IFsw5kq6' // Current Incumbent
        );
        
        if (incumbentAttr?.value) {
          const incumbentValue = incumbentAttr.value;
          
          // Check if it's a staff TEI (looks like a TEI ID)
          if (incumbentValue.length === 11) { // TEI IDs are 11 chars
            positionTeiToStaffTeiMap[positionTeiId] = incumbentValue;
            staffTeiToPositionTeiMap[incumbentValue] = positionTeiId;  
          } else {
            // It's a user ID
            positionToStaffMap[positionTeiId] = incumbentValue;
           
          }
        }
        
      
      }
    });
  }

  // ========== STEP 2: ENHANCED STAFF MAPPING ==========
  if (data.staff?.trackedEntityInstances) {
    console.log('🔄 Processing staff TEIs...');
    data.staff.trackedEntityInstances.forEach(te => {
      if (!te) return;
      
      const staffTeiId = te.trackedEntityInstance;
      
      const staffNameAttr = te.attributes?.find(attr => 
        attr.attribute === 'g3RRIfDiQra' // Staff Name/ID
      );
      
      const userIdAttr = te.attributes?.find(attr => 
        attr.attribute === 'ZHWREkWn5i8' || // Specific user ID attribute
        attr.attribute.includes('user') || 
        attr.attribute.includes('User') ||
        attr.attribute.includes('uid')
      );
      
      if (staffTeiId && staffNameAttr?.value) {
        staffIdToNameMap[staffTeiId] = staffNameAttr.value;
        
        if (userIdAttr?.value) {
          userIdToStaffTeiMap[userIdAttr.value] = staffTeiId;
          staffTeiToUserIdMap[staffTeiId] = userIdAttr.value;
          console.log(`✅ User-Staff Mapping: ${userIdAttr.value} -> ${staffTeiId} (${staffNameAttr.value})`);
        }
      
      }
    });
  }

  // ========== STEP 3: CONNECT USER IDs TO POSITION TEIs ==========
  Object.keys(positionToStaffMap).forEach(positionTeiId => {
    const staffReference = positionToStaffMap[positionTeiId];
    
    if (!staffIdToNameMap[staffReference] && userIdToStaffTeiMap[staffReference]) {
      const staffTei = userIdToStaffTeiMap[staffReference];
      positionTeiToStaffTeiMap[positionTeiId] = staffTei;
      staffTeiToPositionTeiMap[staffTei] = positionTeiId;
      console.log(`🔄 Converted: Position ${positionTeiId} -> User ${staffReference} -> Staff TEI ${staffTei}`);
    }
  });

  // ========== STEP 4: PROCESS OBJECTIVES ==========
  const allItems = [];
  const objectivesMap = {};
  const krasMap = {};
  const kpisMap = {};
  const tasksMap = {};
  const kpisByObjective = {};
  const positionObjectives = {};

  let assignedObjectivesCount = 0;
  let unassignedObjectivesCount = 0;

  // Process OBJECTIVES
  data.objectives?.events?.forEach(event => {
    if (!event) return;

    const dataValues = event.dataValues || [];
    const name = extractName(dataValues, 'VIBtpvkpHFR');
    const description = dataValues.find(dv => dv?.dataElement === 'nWqW5BrcgP1')?.value || '';
    const rawWeight = parseInt(dataValues.find(dv => dv?.dataElement === 'yfb9RCRpMB2')?.value) || 0;
    
    // POSITION-BASED ASSIGNMENT
    let positionTitle = 'Unassigned Position';
    let staffName = 'Unassigned';
    let displayName = 'Unassigned Position';

    const positionTeiId = event.trackedEntityInstance;
    console.log(`🎯 Objective "${name}" -> Position TEI: ${positionTeiId}`);

    if (positionTeiId) {
      positionTitle = positionIdToTitleMap[positionTeiId] || `Position-${positionTeiId.substring(0, 8)}`;
      
      // Get staff through position mapping
      const staffReference = positionTeiToStaffTeiMap[positionTeiId] || positionToStaffMap[positionTeiId];
      if (staffReference) {
        let matchedStaffTei = null;
        let matchedUserId = null;

        // Case 1: staffReference is already a Staff TEI
        if (staffIdToNameMap[staffReference]) {
          matchedStaffTei = staffReference;
          staffName = staffIdToNameMap[matchedStaffTei];
        }
        // Case 2: staffReference is a User ID → map to Staff TEI
        else if (userIdToStaffTeiMap[staffReference]) {
          matchedStaffTei = userIdToStaffTeiMap[staffReference];
          staffName = staffIdToNameMap[matchedStaffTei];
          matchedUserId = staffReference;
        }
        // Case 3: staffReference is a User ID that is the current user
        else if (staffReference === currentUser?.id) {
          staffName = currentUser.name || 'Current User';
          matchedUserId = staffReference;
        }

        if (matchedStaffTei || matchedUserId) {
          displayName = `${positionTitle} (${staffName})`;
          
          if (matchedUserId === currentUser?.id || 
              (matchedStaffTei && staffTeiToUserIdMap[matchedStaffTei] === currentUser?.id)) {
            console.log(`✅ Objective "${name}" -> ${displayName} (Current User: ${currentUser?.name})`);
          } else {
            console.log(`✅ Objective "${name}" -> ${displayName}`);
          }
          assignedObjectivesCount++;
        } else {
          displayName = `${positionTitle} (Unmapped: ${staffReference})`;
          unassignedObjectivesCount++;
          console.log(`🟡 Objective "${name}" -> ${displayName}`);
        }
      } else {
        displayName = `${positionTitle} (Vacant)`;
        unassignedObjectivesCount++;
      }
    } else {
      // No position assigned
      unassignedObjectivesCount++;
    }

    const item = {
      id: event.event,
      objectiveId: event.event,
      programStage: 'VIBtpvkpHFR',
      name,
      description,
      rawWeight,
      type: 'objective',
      children: [],
      assignedTo: displayName,
      positionTitle,
      staffName,
      orgUnitName: event.orgUnitName,
      orgUnit: event.orgUnit,
      calculatedProgress: 0,
      taskCount: 0,
      kpiCount: 0,
      positionTeiId: positionTeiId,
      enrollmentId: event.enrollment,
      status: 'Not Started',
    };

    objectivesMap[event.event] = item;
    
    // Group by position assignment
    if (positionTitle !== 'Unassigned Position') {
      if (!positionObjectives[displayName]) {
        positionObjectives[displayName] = [];
      }
      positionObjectives[displayName].push(item);
    }
    
    allItems.push(item);
  });

  console.log('📊 Objective assignment:', {
    total: data.objectives?.events?.length || 0,
    assigned: assignedObjectivesCount,
    unassigned: unassignedObjectivesCount
  });

  // ========== STEP 5: PROCESS KRAs ==========
  data.kras?.events?.forEach(event => {
    if (!event) return;
    
    const dataValues = event.dataValues || [];
    const objectiveId = dataValues.find(dv => dv?.dataElement === 'plJovUlxfQ0')?.value;
    
    if (objectiveId && objectivesMap[objectiveId]) {
      const parentObjective = objectivesMap[objectiveId];
      
      const item = {
        id: event.event,
        objectiveId,
        programStage: 'BJb3qTgbcTF',
        name: extractName(dataValues, 'BJb3qTgbcTF'),
        type: 'kra',
        children: [],
        assignedTo: parentObjective.assignedTo
      };
      krasMap[event.event] = item;
      allItems.push(item);
    }
  });

  // ========== STEP 6: PROCESS KPIs ==========
  data.kpis?.events?.forEach(event => {
    if (!event) return;
    
    const dataValues = event.dataValues || [];
    const objectiveId = dataValues.find(dv => dv?.dataElement === 'plJovUlxfQ0')?.value;
    
    if (objectiveId && objectivesMap[objectiveId]) {
      const parentObjective = objectivesMap[objectiveId];
      
      const item = {
        id: event.event,
        objectiveId,
        programStage: 'ivyCLjNLFrd',
        name: extractName(dataValues, 'ivyCLjNLFrd'),
        type: 'kpi',
        children: [],
        assignedTo: parentObjective.assignedTo,
        kpiTarget: dataValues.find(dv => dv?.dataElement === 'Ru68Ax2Xo2L')?.value,
        kpiLinkageId: dataValues.find(dv => dv?.dataElement === 'uXeH0X0aaCl')?.value
      };
      kpisMap[event.event] = item;
      
      if (!kpisByObjective[objectiveId]) {
        kpisByObjective[objectiveId] = [];
      }
      kpisByObjective[objectiveId].push(item);
      
      allItems.push(item);
    }
  });

  // ========== STEP 7: TASK EXTRACTION ==========
  const extractTasksWithTEIs = (tasksData) => {
    if (!tasksData?.events) return [];
    
    return tasksData.events.map(event => {
      const dataValues = event.dataValues || [];
      
      const name = dataValues.find(dv => dv.dataElement === 'I0QtZkMbzxN')?.value || 'Unnamed Task';
      const progress = parseInt(dataValues.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0;
      const status = dataValues.find(dv => dv.dataElement === 'LuTwfuN34pc')?.value || 'not_started';
      const objectiveLink = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      const kpiId = dataValues.find(dv => dv.dataElement === 'uXeH0X0aaCl')?.value || '';
      const weekEnding = dataValues.find(dv => dv.dataElement === 'EiL3lPaL908')?.value || '';
      const achievements = dataValues.find(dv => dv.dataElement === 'j9FdNxmPpXM')?.value || '';
      const challenges = dataValues.find(dv => dv.dataElement === 'WSQ9nuXPnNL')?.value || '';
      const nextSteps = dataValues.find(dv => dv.dataElement === 'M7PG2p2XsDA')?.value || '';
      
      const task = {
        id: event.event,
        name,
        progress,
        status: getStatusFromValue(status),
        dueDate: dataValues.find(dv => dv.dataElement === 'IY0xyOmlEr7')?.value || '',
        tei: event.trackedEntityInstance,
        objectiveLink: objectiveLink,
        kpiId: kpiId,
        weekEnding: weekEnding,
        achievements: achievements,
        challenges: challenges,
        nextSteps: nextSteps,
        orgUnit: event.orgUnit,
        orgUnitName: event.orgUnitName,
        eventDate: event.eventDate,
        lastUpdated: new Date().toISOString()
      };
      
      return task;
    });
  };

  const getStatusFromValue = (statusValue) => {
    const statusMap = {
      'completed': 'On Track',
      'inprogress': 'At Risk', 
      'in_progress': 'At Risk',
      'not_started': 'Behind Schedule',
      'pending': 'Behind Schedule',
      'planning': 'Behind Schedule'
    };
    return statusMap[statusValue] || statusValue;
  };

  // ========== STEP 8: ENHANCED TASK LINKING ==========
  const processTasksWithEnhancedLinking = () => {
    console.log('🔄 Processing TASKS with enhanced linking...');
    const allTasks = extractTasksWithTEIs(data.tasks);
    
    allTasks.forEach(task => {
      let linkedObjective = null;
      let linkingMethod = '';
      
      // Strategy 1: Direct objective linkage via data element
      if (task.objectiveLink) {
        linkedObjective = objectivesMap[task.objectiveLink];
        if (linkedObjective) {
          linkingMethod = 'direct linkage';
          console.log(`✅ Task "${task.name}" linked to objective "${linkedObjective.name}" via direct linkage`);
        }
      }
      
      // Strategy 2: Direct position TEI matching
      if (!linkedObjective && task.tei) {
        // Check if task TEI is a position TEI
        linkedObjective = Object.values(objectivesMap).find(obj => 
          obj.positionTeiId && obj.positionTeiId === task.tei
        );
        
        if (linkedObjective) {
          linkingMethod = 'position TEI match';
          console.log(`✅ Task "${task.name}" linked to objective "${linkedObjective.name}" via position TEI match`);
        }
      }
      
      // Strategy 3: Task TEI is a staff TEI that has a position
      if (!linkedObjective && task.tei && staffTeiToPositionTeiMap[task.tei]) {
        const positionTei = staffTeiToPositionTeiMap[task.tei];
        linkedObjective = Object.values(objectivesMap).find(obj => 
          obj.positionTeiId === positionTei
        );
        
        if (linkedObjective) {
          linkingMethod = 'staff->position chain';
          console.log(`✅ Task "${task.name}" linked to objective "${linkedObjective.name}" via staff->position chain`);
        }
      }
      
      // Strategy 4: Task has position TEI that exists in position mapping
      if (!linkedObjective && task.tei) {
        // Try to find if this TEI exists in any position mapping
        const positionTei = Object.keys(positionIdToTitleMap).find(posTei => 
          posTei === task.tei
        );
        
        if (positionTei) {
          linkedObjective = Object.values(objectivesMap).find(obj => 
            obj.positionTeiId === positionTei
          );
          
          if (linkedObjective) {
            linkingMethod = 'TEI position lookup';
            console.log(`✅ Task "${task.name}" linked to objective "${linkedObjective.name}" via TEI position lookup`);
          }
        }
      }
      
      if (linkedObjective) {
        const item = {
          id: task.id,
          objectiveId: linkedObjective.objectiveId,
          programStage: 'Nv9nYNcBTdM',
          name: task.name,
          type: 'task',
          children: [],
          assignedTo: linkedObjective.assignedTo,
          taskCompletion: task.progress,
          taskStatus: task.status,
          taskPriority: 'Medium',
          staffTeiId: task.tei,
          positionTeiId: linkedObjective.positionTeiId,
          linkingMethod: linkingMethod,
          kpiId: task.kpiId,
          weekEnding: task.weekEnding,
          achievements: task.achievements,
          challenges: task.challenges,
          nextSteps: task.nextSteps,
          lastUpdated: task.lastUpdated || new Date().toISOString()
        };
        
        if (!tasksMap[linkedObjective.objectiveId]) {
          tasksMap[linkedObjective.objectiveId] = [];
        }
        tasksMap[linkedObjective.objectiveId].push(item);
        allItems.push(item);
      } else {
        console.log(`❌ Could not link task: ${task.name} (TEI: ${task.tei})`);
        console.log(`   Available position TEIs: ${Object.keys(positionIdToTitleMap).join(', ')}`);
        console.log(`   Available staff TEIs: ${Object.keys(staffIdToNameMap).join(', ')}`);
      }
    });
  };

  processTasksWithEnhancedLinking();

  // ========== STEP 9: PROGRESS CALCULATION ==========
  const calculateKPIProgressForObjective = (kpis, tasks) => {
    if (kpis.length === 0 || tasks.length === 0) return 0;
    
    let totalKPIProgress = 0;
    let kpisWithTasks = 0;
    
    kpis.forEach(kpi => {
      const kpiTasks = tasks.filter(task => task.kpiId === kpi.kpiLinkageId);
      if (kpiTasks.length > 0) {
        const kpiProgress = kpiTasks.reduce((sum, task) => sum + (task.taskCompletion || 0), 0) / kpiTasks.length;
        totalKPIProgress += kpiProgress;
        kpisWithTasks++;
      }
    });
    
    return kpisWithTasks > 0 ? Math.round(totalKPIProgress / kpisWithTasks) : 0;
  };

  const calculateWeightedProgress = (objective) => {
    const taskWeight = 0.6;
    const kpiWeight = 0.4;
    
    const taskProgress = objective.calculatedProgress || 0;
    const kpiProgress = objective.kpiProgress || 0;
    
    if (objective.kpiCount === 0) {
      return taskProgress;
    }
    
    return Math.round((taskProgress * taskWeight) + (kpiProgress * kpiWeight));
  };

  const calculateEnhancedProgress = () => {
    console.log('📊 Calculating enhanced progress metrics...');
    
    Object.values(objectivesMap).forEach(objective => {
      if (!objective) return;
      
      const objectiveTasks = tasksMap[objective.objectiveId] || [];
      const objectiveKPIs = kpisByObjective[objective.objectiveId] || [];
      
      if (objectiveTasks.length > 0) {
        const totalCompletion = objectiveTasks.reduce((sum, task) => sum + (task?.taskCompletion || 0), 0);
        objective.calculatedProgress = Math.round(totalCompletion / objectiveTasks.length);
        objective.taskCount = objectiveTasks.length;
        objective.completedTasks = objectiveTasks.filter(task => task?.taskCompletion === 100).length;
        
        objective.kpiProgress = calculateKPIProgressForObjective(objectiveKPIs, objectiveTasks);
        objective.weightedProgress = calculateWeightedProgress(objective);
      } else {
        objective.calculatedProgress = 0;
        objective.taskCount = 0;
        objective.completedTasks = 0;
        objective.kpiProgress = 0;
        objective.weightedProgress = 0;
      }
      
      objective.kpiCount = objectiveKPIs.length;
      objective.kpisWithTasks = objectiveKPIs.filter(kpi => 
        objectiveTasks.some(task => task.kpiId === kpi.kpiLinkageId)
      ).length;
      
      console.log(`📈 Objective "${objective.name}": ${objective.calculatedProgress}% (${objective.taskCount} tasks, ${objective.kpiCount} KPIs)`);
      objective.status = calculateObjectiveStatus(objective.calculatedProgress, objective.taskCount > 0);
    });
  };

  calculateEnhancedProgress();

  // ========== STEP 10: BUILD HIERARCHY ==========
  const rootItems = [];
  Object.values(objectivesMap).forEach(objective => {
    if (!objective) return;
    
    const relatedKras = Object.values(krasMap).filter(kra => kra?.objectiveId === objective.objectiveId);
    const relatedKpis = Object.values(kpisMap).filter(kpi => kpi?.objectiveId === objective.objectiveId);
    const relatedTasks = tasksMap[objective.objectiveId] || [];

    relatedKras.forEach(kra => objective.children.push(kra));
    relatedKpis.forEach(kpi => objective.children.push(kpi));
    relatedTasks.forEach(task => objective.children.push(task));
    
    rootItems.push(objective);
  });

  // ========== STEP 11: POSITION METRICS ==========
  const positionMetrics = Object.keys(positionObjectives)
    .map(position => {
      const objectives = positionObjectives[position] || [];
      
      const allPositionTasks = objectives.flatMap(obj => tasksMap[obj?.objectiveId] || []);
      const totalTasks = allPositionTasks.length;
      const completedTasks = allPositionTasks.filter(task => task?.taskCompletion === 100).length;
      const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

      const overallProgress = objectives.length > 0 
        ? Math.round(objectives.reduce((sum, obj) => sum + (obj.weightedProgress || obj.calculatedProgress || 0), 0) / objectives.length)
        : 0;

      const kpiCount = objectives.flatMap(obj => kpisByObjective[obj.objectiveId] || []).length;
      const kpisWithTasks = objectives.flatMap(obj => 
        (kpisByObjective[obj.objectiveId] || []).filter(kpi => 
          (tasksMap[obj.objectiveId] || []).some(task => task.kpiId === kpi.kpiLinkageId)
        )
      ).length;

      const kpiCoverage = kpiCount > 0 ? Math.round((kpisWithTasks / kpiCount) * 100) : 0;

      return {
      position,
      objectiveCount: objectives.length,
      overallProgress,
      taskCount: totalTasks,
      completedTasks,
      completionRate,
      kpiCount: kpiCount,
      kpisWithTasks: kpisWithTasks,
      kpiCoverage: kpiCoverage,
      objectives: objectives,
      recentTasks: allPositionTasks.filter(task => {
        const taskDate = new Date(task.lastUpdated || task.eventDate);
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return taskDate > weekAgo;
      }).length
    };
  })
  .filter(metric => 
    metric.objectiveCount > 0 && 
    !metric.position.includes('(Unmapped:') && 
    !metric.position.includes('(Vacant)')
  );

  console.log('📊 Position Metrics:', positionMetrics.map(p => ({
    position: p.position,
    objectives: p.objectiveCount,
    tasks: p.taskCount,
    progress: p.overallProgress
  })));

  // ========== STEP 12: CALCULATE SUMMARY ==========
  const allTasks = Object.values(tasksMap).flat().filter(Boolean);
  const completedTasks = allTasks.filter(task => task?.taskCompletion === 100).length;
  const taskCompletionRate = allTasks.length > 0 ? Math.round((completedTasks / allTasks.length) * 100) : 0;

  const overallProgress = positionMetrics.length > 0 
    ? Math.round(positionMetrics.reduce((sum, metric) => sum + metric.overallProgress, 0) / positionMetrics.length)
    : 0;

  const totalKPIs = Object.values(kpisByObjective).flat().length;
  const kpisWithTasks = Object.values(kpisByObjective).flat().filter(kpi => 
    allTasks.some(task => task.kpiId === kpi.kpiLinkageId)
  ).length;
  const kpiCoverage = totalKPIs > 0 ? Math.round((kpisWithTasks / totalKPIs) * 100) : 0;

  const recentTasks = allTasks.filter(task => {
    const taskDate = new Date(task.lastUpdated || task.eventDate);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return taskDate > weekAgo;
  }).length;

  const summary = {
    totalPositions: positionMetrics.length,
    totalObjectives: rootItems.length,
    totalTasks: allTasks.length,
    completedTasks,
    taskCompletionRate,
    overallProgress,
    positionMetrics,
    kpiCoverage,
    totalKPIs,
    kpisWithTasks,
    recentTasks,
    kpiTaskEffectiveness: kpiCoverage
  };

  console.log('🎉 FINAL processed data:', {
    objectives: rootItems.length,
    tasks: allTasks.length,
    positions: positionMetrics.length,
    summary
  });

  console.log('🔍 TASK COUNT DEBUG:', {
    totalTasksInMap: Object.values(tasksMap).flat().length,
    tasksByObjective: Object.keys(tasksMap).map(objId => ({
      objectiveId: objId,
      taskCount: tasksMap[objId].length,
      objectiveName: objectivesMap[objId]?.name
    })),
    positionMetrics: positionMetrics.map(p => ({
      position: p.position,
      taskCount: p.taskCount,
      objectives: p.objectives.map(obj => ({
        name: obj.name,
        taskCount: tasksMap[obj.objectiveId]?.length || 0
      }))
    }))
  });

  return {
    hierarchy: rootItems,
    positionMetrics,
    summary,
    tasksByObjective: tasksMap,
    kpisByObjective
  };
}, [data, orgUnitId, currentUser]);


  const toggleExpand = (itemId) => {
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId)
    } else {
      newExpanded.add(itemId)
    }
    setExpandedItems(newExpanded)
  }

  const getTypeIcon = (type) => {
    const icons = {
      goal: '🏆',
      objective: '🎯',
      kra: '📊',
      kpi: '📈',
      task: '✅'
    }
    return icons[type] || '📝'
  }

  const getTypeColor = (type) => {
    const colors = {
      goal: '#7c3aed',
      objective: '#4f46e5',
      kra: '#059669',
      kpi: '#dc2626',
      task: '#f59e0b'
    }
    return colors[type] || '#6b7280'
  }

  const getTaskPriorityColor = (priority) => {
    const colors = {
      'High': '#dc2626',
      'Medium': '#f59e0b',
      'Low': '#059669'
    }
    return colors[priority] || '#6b7280'
  }

  const getProgressColor = (value) => {
    if (value >= 80) return '#059669' // green
    if (value >= 40) return '#f59e0b' // yellow
    return '#dc2626' // red
  }

  const ProgressBar = ({ value, height = '6px', borderRadius = '3px' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius
    }}>
      <div style={{
        width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
        height: '100%',
        backgroundColor: getProgressColor(value || 0),
        borderRadius,
        transition: 'width 0.3s ease'
      }} />
    </div>
  )

  // Hierarchy View
  const renderHierarchyView = () => {
    const renderHierarchyItem = (item, depth = 0) => {
      const hasChildren = item.children && item.children.length > 0
      const isExpanded = expandedItems.has(item.id)
      const indent = Math.min(depth * 20, 80)

      const isTask = item.type === 'task'
      const taskCompletion = item.taskCompletion || 0

      return (
        <div key={item.id} className="hierarchy-item" style={{
          borderBottom: '1px solid #f3f4f6',
          overflow: 'hidden'
        }}>
          <div 
            className="hierarchy-item-header"
            style={{ 
              paddingLeft: `${8 + indent}px`,
              paddingRight: '8px',
              paddingTop: '12px',
              paddingBottom: '12px',
              minHeight: '60px',
              cursor: hasChildren ? 'pointer' : 'default',
              backgroundColor: isTask ? '#fffbeb' : 'transparent',
              borderLeft: isTask ? `4px solid ${getTaskPriorityColor(item.taskPriority)}` : 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
            onClick={() => hasChildren && toggleExpand(item.id)}
          >
            {/* Main Content Row */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '8px',
              width: '100%'
            }}>
              {/* Expand Button and Type Icon */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                flexShrink: 0
              }}>
                {hasChildren ? (
                  <button 
                    className="expand-button"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleExpand(item.id)
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '24px',
                      minHeight: '24px'
                    }}
                  >
                    <ExpandIcon expanded={isExpanded} />
                  </button>
                ) : (
                  <div style={{ width: '24px' }} />
                )}
                
                <span 
                  className="type-icon"
                  style={{ 
                    backgroundColor: getTypeColor(item.type),
                    width: '32px',
                    height: '32px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    flexShrink: 0
                  }}
                >
                  {getTypeIcon(item.type)}
                </span>
              </div>

              {/* Content */}
              <div style={{ 
                flex: 1, 
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '4px'
              }}>
                {/* Title Row */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  gap: '6px',
                  flexWrap: 'wrap'
                }}>
                  <span className="item-name" style={{ 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#1f2937',
                    lineHeight: '1.3',
                    wordBreak: 'break-word'
                  }}>
                    {item.name}
                  </span>
                  
                  {/* Badges */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '4px', 
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    <span className="item-type-badge" style={{ 
                      backgroundColor: getTypeColor(item.type),
                      fontSize: '9px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      color: 'white',
                      whiteSpace: 'nowrap'
                    }}>
                      {item.type.toUpperCase()}
                    </span>
                    
                    {isTask && item.taskPriority && (
                      <span style={{ 
                        fontSize: '9px',
                        padding: '2px 6px',
                        backgroundColor: getTaskPriorityColor(item.taskPriority),
                        color: 'white',
                        borderRadius: '4px',
                        whiteSpace: 'nowrap'
                      }}>
                        {item.taskPriority}
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {item.description && item.description !== item.name && (
                  <div className="item-description" style={{ 
                    fontSize: '12px', 
                    color: '#6b7280',
                    lineHeight: '1.3',
                    wordBreak: 'break-word'
                  }}>
                    {item.description}
                  </div>
                )}

                {/* Metadata Row */}
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px',
                  alignItems: 'center',
                  fontSize: '11px',
                  color: '#6b7280'
                }}>
                  {item.assignedTo && item.assignedTo !== 'Unassigned Position' && (
                    <span style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      👤 {item.assignedTo}
                    </span>
                  )}
                  
                  {item.normalizedWeight && (
                    <span>
                      {item.normalizedWeight}% weight
                    </span>
                  )}
                  
                  {item.taskCount > 0 && (
                    <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                      {item.taskCount} tasks ({item.completedTasks || 0} completed)
                    </span>
                  )}
                </div>

                {/* Task progress bar */}
                {isTask && (
                  <div style={{ marginTop: '4px' }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <ProgressBar value={taskCompletion} />
                      </div>
                      <span style={{ 
                        fontWeight: '600', 
                        minWidth: '35px',
                        fontSize: '11px'
                      }}>
                        {taskCompletion}%
                      </span>
                    </div>
                    <div style={{ 
                      fontSize: '10px', 
                      color: '#6b7280',
                      marginTop: '2px'
                    }}>
                      Status: {item.taskStatus || 'Not Started'}
                    </div>
                  </div>
                )}
              </div>

              {/* Status and Progress */}
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'flex-end',
                gap: '4px',
                flexShrink: 0,
                minWidth: '60px'
              }}>
                {!isTask && (
                  <div style={{ transform: 'scale(0.8)', transformOrigin: 'top right' }}>
                    <StatusBadge status={item.status} />
                  </div>
                )}
                <div className="progress-display" style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '2px',
                  fontSize: '11px'
                }}>
                  <span style={{ 
                    fontWeight: '600',
                    color: getProgressColor(item.calculatedProgress || 0)
                  }}>
                    {item.calculatedProgress || 0}%
                  </span>
                  {item.weightedProgress > 0 && (
                    <span style={{ 
                      color: '#059669', 
                      fontWeight: '600',
                      fontSize: '9px'
                    }}>
                      {item.weightedProgress}% weighted
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Children */}
          {hasChildren && isExpanded && (
            <div className="hierarchy-children" style={{ 
              backgroundColor: depth === 0 ? '#fafbfc' : 'transparent',
              borderLeft: depth > 0 ? `2px solid #e5e7eb` : 'none',
              marginLeft: `${indent + 8}px`
            }}>
              {item.children.map(child => renderHierarchyItem(child, depth + 1))}
            </div>
          )}
        </div>
      )
    }

    return (
      <div className="hierarchy-view" style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: 'white'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <h4 style={{ 
            margin: 0, 
            fontSize: '16px',
            fontWeight: '600',
            color: '#374151'
          }}>
            Performance Hierarchy
          </h4>
          <div style={{ 
            fontSize: '12px', 
            color: '#6b7280',
            marginTop: '4px'
          }}>
            {processedData.hierarchy.length} objectives • Click to expand/collapse
          </div>
        </div>

        {/* Hierarchy Items */}
        <div style={{
          maxHeight: '600px',
          overflowY: 'auto'
        }}>
          {processedData.hierarchy.length > 0 ? (
            processedData.hierarchy.map(item => renderHierarchyItem(item))
          ) : (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No objectives found in the current organization unit.
            </div>
          )}
        </div>
      </div>
    )
  }

  // Position Performance View (replacing Staff Performance)
  const renderPositionView = () => {
  return (
    <div>
      {/* Search Bar */}
      <div style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        marginBottom: '16px'
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{
            position: 'absolute',
            left: '16px',
            color: '#9ca3af',
            pointerEvents: 'none',
          }}>
            🔍
          </span>
          <input
            type="text"
            placeholder="Search positions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px 12px 40px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              fontSize: '16px',
              outline: 'none',
              transition: 'border-color 0.2s', 
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
      </div>

      {/* Position Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '16px',
        width: '100%'
      }}>
        {processedData.positionMetrics
          .filter(metric => 
            metric.position.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map(metric => (
            <div key={metric.position} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#ffffff',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s, box-shadow 0.2s',
              minHeight: '220px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  margin: '0',
                  fontWeight: '600',
                  color: '#111827',
                  fontSize: '1rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {metric.position}
                </h3>
                 <StatusBadge 
                      status={calculateObjectiveStatus(metric.overallProgress, metric.taskCount > 0)} 
                    />
              </div>
              
              <div style={{
                marginBottom: '8px',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                <strong style={{color: '#111827'}}>Objectives:</strong> {metric.objectiveCount}
              </div>
              
              <div style={{
                marginBottom: '8px',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                <strong style={{color: '#111827'}}>KPIs:</strong> {metric.kpiCount} 
                {metric.kpisWithTasks > 0 && (
                  <span style={{color: '#059669', fontSize: '0.75rem', marginLeft: '4px'}}>
                    ({metric.kpisWithTasks} with tasks)
                  </span>
                )}
              </div>
              
              <div style={{
                marginBottom: '8px',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                <strong style={{color: '#111827'}}>Total Tasks:</strong> 
                <span style={{
                  color: metric.taskCount > 0 ? '#059669' : '#6b7280',
                  fontWeight: '600',
                  marginLeft: '4px'
                }}>
                  {metric.taskCount}
                </span>
              </div>
              
              <div style={{
                marginBottom: '8px',
                fontSize: '0.875rem',
                color: '#374151'
              }}>
                <strong style={{color: '#111827'}}>Completed Tasks:</strong>
                <span style={{
                  color: metric.completedTasks > 0 ? '#059669' : '#6b7280',
                  fontWeight: '600',
                  marginLeft: '4px'
                }}>
                  {metric.completedTasks}
                </span>
              </div>
              
              <div style={{
                marginBottom: '8px'
              }}>
                <strong style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  Task Completion Rate:
                </strong>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <ProgressBar value={metric.completionRate} />
                  <span style={{
                    fontWeight: '600',
                    minWidth: '35px',
                    fontSize: '0.75rem',
                    color: '#111827'
                  }}>
                    {metric.completionRate}%
                  </span>
                </div>
              </div>
              
              <div>
                <strong style={{
                  display: 'block',
                  marginBottom: '4px',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  Overall Progress:
                </strong>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <ProgressBar value={metric.overallProgress} />
                  <span style={{
                    fontWeight: '600',
                    minWidth: '40px',
                    fontSize: '0.75rem',
                    color: '#111827'
                  }}>
                    {metric.overallProgress}%
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  )
}

  // Summary View with Position Metrics
  const renderSummaryView = () => {
    const { summary } = processedData
    
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Summary Cards Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '16px', 
        }}>
          {/* Positions */}
          <Card style={{ 
            padding: '24px', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f0f4ffff 0%, #e0f2fe 100%)',
            boxShadow: '0 8px 24px rgba(79, 70, 229, 0.15)',
            border: '1px solid rgba(79, 70, 229, 0.2)',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#4f46e5',
              marginBottom: '8px',  margin: '10px'
            }}>
              {summary.totalPositions}
            </div>
             <hr style={{ width: '80%', border: '1px solid #8d94f3ff', margin: '10px 10px 10px 10px' }} />
            <div style={{ color: '#4338ca', fontSize: '14px', fontWeight: '500',  margin: '10px'  }}>
              Positions
            </div>
          </Card>
          
          {/* Total Objectives */}
          <Card style={{ 
            padding: '24px', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
            boxShadow: '0 8px 24px rgba(5, 150, 105, 0.15)',
            border: '1px solid rgba(5, 150, 105, 0.2)',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#059669',
              marginBottom: '8px',  margin: '10px'
            }}>
              {summary.totalObjectives}
            </div>
            <hr style={{ width: '80%', border: '1px solid #a1f2a7ff', margin: '10px 10px 10px 10px' }} />
            <div style={{ color: '#047857', fontSize: '14px', fontWeight: '500',  margin: '10px' }}>
              Total Objectives
            </div>
          </Card>

          {/* KPI Coverage */}
          <Card style={{ 
            padding: '24px', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            boxShadow: '0 8px 24px rgba(220, 38, 38, 0.15)',
            border: '1px solid rgba(220, 38, 38, 0.2)',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#dc2626',
              marginBottom: '8px',  margin: '10px'
            }}>
              {summary.kpiCoverage}%
            </div>
            <hr style={{ width: '80%', border: '1px solid #f99999ff', margin: '10px 10px 10px 10px' }} />
            <div style={{ color: '#b91c1c', fontSize: '14px', fontWeight: '500',  margin: '10px' }}>
              KPI-Task Coverage
            </div>
          </Card>

          {/* KPI-Task Effectiveness */}
          <Card style={{ 
            padding: '24px', 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            boxShadow: '0 8px 24px rgba(14, 165, 233, 0.15)',
            border: '1px solid rgba(14, 165, 233, 0.2)',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold', 
              color: '#0ea5e9',
              marginBottom: '8px',  
              margin: '10px'
            }}>
              {summary.kpiTaskEffectiveness || 0}%
            </div>
            <hr style={{ width: '80%', border: '1px solid #7dd3fc', margin: '10px 10px 10px 10px' }} />
            <div style={{ color: '#0284c7', fontSize: '14px', fontWeight: '500', margin: '10px' }}>
              KPI-Task Linkage
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              {summary.kpisWithTasks || 0}/{summary.totalKPIs || 0} KPIs linked
            </div>
          </Card>

        {/* Recent Activity */}
        <Card style={{ 
          padding: '24px', 
          textAlign: 'center',
          background: 'linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%)',
          boxShadow: '0 8px 24px rgba(236, 72, 153, 0.15)',
          border: '1px solid rgba(236, 72, 153, 0.2)',
          borderRadius: '12px',
          transition: 'all 0.3s ease'
        }}>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: '#ec4899',
            marginBottom: '8px',  
            margin: '10px'
          }}>
            {summary.recentTasks || 0}
          </div>
          <hr style={{ width: '80%', border: '1px solid #f9a8d4', margin: '10px 10px 10px 10px' }} />
          <div style={{ color: '#db2777', fontSize: '14px', fontWeight: '500', margin: '10px' }}>
            Recent Tasks (7d)
          </div>
        </Card>

          {/* Task Completion */}
          <Card style={{ 
              padding: '24px', 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              boxShadow: '0 8px 24px rgba(5, 150, 105, 0.15)',
              border: '1px solid rgba(5, 150, 105, 0.2)',
              borderRadius: '12px',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: '#9e12eaff',
                marginBottom: '8px',  margin: '10px'
              }}>
                {summary.taskCompletionRate}%
              </div>
              <hr style={{ width: '80%', border: '1px solid #ea95efff', margin: '10px 10px 10px 10px' }} />
              <div style={{ color: '#9e12eaff', fontSize: '14px', fontWeight: '500',  margin: '10px' }}>
                Task Completion
              </div>
              <ProgressBar value={summary.taskCompletionRate} height="10px" />
            </Card>
                      
            {/* Overall Progress */}
            <Card style={{ 
              padding: '24px', 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
              boxShadow: '0 8px 24px rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: '12px',
              transition: 'all 0.3s ease'
            }}>
              <div style={{ 
                fontSize: '28px', 
                fontWeight: 'bold', 
                color: '#f59e0b',
                marginBottom: '8px',
                margin: '10px'
              }}>
                {summary.overallProgress}%
              </div>
              <hr style={{ width: '80%', border: '1px solid #f7e3a2ff', margin: '10px 10px 10px 10px' }} />
              <div style={{ color: '#d97706', fontSize: '14px', fontWeight: '500',  margin: '10px' }}>
                Overall Progress
              </div>
              <ProgressBar value={summary.overallProgress} height="10px" />
            </Card>
        </div>

        {/* Progress by Position */}
        <Card style={{ 
          padding: '24px',
          gridColumn: '1 / -1'
        }}>
          
          
          {/* Position Progress Grid */}
           <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: '16px',
                padding: '16px',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                marginBottom: '30px'
              }}>
                {summary.positionMetrics
                  .filter(metric => 
                    metric.position.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map(metric => (
              <div key={metric.position} style={{ 
                padding: '16px',
                border: '1px solid #f3f4f6',
                borderRadius: '8px',
                backgroundColor: '#eaf7ecff',
                transition: 'all 0.2s',
                boxShadow: "0 8px 10px rgba(173, 157, 157, 1)"
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '8px'
                }}>
                  <span style={{ 
                    fontWeight: '600', 
                    fontSize: '14px' 
                  }}>
                    {metric.position}
                  </span>
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    color: '#4f46e5'
                  }}>
                    {metric.overallProgress}%
                  </span>
             </div>
             
            </div>
          ))}

          </div>
        </Card>
      </div>
    );
  }

  if (loading) return (
    <Card style={{ padding: 16 }}>
      <h3>Performance Objectives Dashboard</h3>
      <ProgressBar value={50} />
      <div style={{ textAlign: 'center', marginTop: 8 }}>Loading performance data...</div>
    </Card>
  )
  
  if (error) {
    return (
      <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
        <h3>Performance Objectives Dashboard</h3>
        <div style={{ color: '#721c24' }}>
          Error loading performance data: {error.message}
        </div>
      </Card>
    )
  }

  return (
    <Card style={{ padding: 16, boxShadow: "0 8px 10px rgba(173, 157, 157, 1)", }}>
      <div className="objectives-header">
        <div>
          <h3 style={{ marginBottom: '8px', marginLeft: '20px' }}>Performance Objectives Dashboard</h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setViewMode('summary')}
              style={{ 
                padding: '8px 16px', 
                fontSize: '14px', 
                background: viewMode === 'summary' ? '#04b10aff' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
                marginLeft: '20px'
              }}
            >
              Overall Summary
            </button>
            <button 
              onClick={() => setViewMode('hierarchy')}
              style={{ 
                padding: '8px 16px', 
                fontSize: '14px', 
                background: viewMode === 'hierarchy' ? '#04b10aff' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
              }}
            >
              Hierarchy View
            </button>
            <button 
              onClick={() => setViewMode('position')}
              style={{ 
                padding: '8px 16px', 
                fontSize: '14px', 
                background: viewMode === 'position' ? '#04b10aff' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                boxShadow: "0 8px 10px rgba(173, 157, 157, 1)",
              }}
            >
              Position Performance
            </button>
          </div>
        </div>
      </div>

      {/* Enhanced Summary Stats Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '20px',
        marginLeft: '20px',
        marginRight: '20px',
        padding: '12px',
        backgroundColor: '#deedcbff',
        borderRadius: '8px',
        flexWrap: 'wrap',
        boxShadow: "0 8px 10px rgba(217, 213, 213, 1)",
        border: '2px solid #fff)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>Positions:</div>
          <div>{processedData.summary?.totalPositions || 0}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>Objectives:</div>
          <div>{processedData.summary?.totalObjectives || 0}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>KPIs:</div>
          <div>{Object.values(processedData.kpisByObjective || {}).flat().length || 0}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>Tasks:</div>
          <div>{processedData.summary?.totalTasks || 0}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>KPI Coverage:</div>
          <div style={{ color: '#059669', fontWeight: '600' }}>
            {processedData.summary?.kpiCoverage || 0}%
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ fontWeight: '600' }}>Overall Progress:</div>
          <div style={{ fontWeight: '600', color: '#059669' }}>
            {processedData.summary?.overallProgress || 0}%
          </div>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'hierarchy' && renderHierarchyView()}
      {viewMode === 'position' && renderPositionView()}
      {viewMode === 'summary' && renderSummaryView()}
    </Card>
  )
}

export default Objectives
